import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config.js';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { 
  transformToNivoLine, 
  transformToNivoBar, 
  transformToNivoPie,
  getNivoBarKeys,
  getNivoBarIndexBy
} from '../../utils/nivoTransformers.js';
import { getNivoTheme, getChartColors, getNivoCommonProps } from '../../utils/nivoTheme.js';
import { formatValueByType } from '../../utils/chartHelpers.js';
import {
  calculateLegendMargin,
  hasNegativeValues,
  determineXScale,
  createZeroLineMarker,
  getStandardLegendConfig,
  getStandardAxisConfig,
  formatAxisNumber,
  formatXAxisInteger,
  formatYAxisValue,
  TooltipWrapper
} from '../../utils/chartUtils.jsx';
import AddToDashboardButton from './AddToDashboardButton.jsx';
import './ChartRenderer.css';
import './ChartTypeSelector.css';

/**
 * Component for rendering different types of charts using Nivo
 * @param {Object} props - Component props
 * @param {Array} props.data - Chart data
 * @param {string} props.chartType - Type of chart to render
 * @param {Array} props.columns - Column names
 * @param {string} props.dataType - Type of data
 * @param {Array} props.chartTypes - Available chart types
 * @param {Object} props.metadata - Metadata including granularity
 * @param {Object} props.columnDisplayNames - Mapping of column names to display names
 * @param {Object} props.columnValueTypes - Mapping of column names to value types
 * @param {Object} props.chartConfig - Explicit chart configuration from backend
 * @param {Function} props.onChartTypeChange - Function to change chart type
 * @param {number} props.queryId - Query ID for adding to dashboard
 * @param {boolean} props.disabled - Whether add to dashboard is disabled
 * @param {boolean} props.hideHeader - Whether to hide the chart header (for modal mode)
 * @returns {JSX.Element} Chart component
 */
const NivoChartRenderer = ({ 
  data, 
  chartType, 
  columns, 
  dataType, 
  chartTypes, 
  metadata, 
  columnDisplayNames, 
  columnValueTypes, 
  chartConfig,
  onChartTypeChange, 
  queryId, 
  disabled,
  hideHeader = false,
  stepSelector = null
}) => {
  const { t } = useTranslation();
  
  // Get design system values for tooltips
  const getDesignSystemValue = (varName) => {
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  };
  
  // Tooltip styles using design system
  const tooltipStyles = {
    container: {
      background: 'white',
      padding: `${getDesignSystemValue('--spacing-3')} ${getDesignSystemValue('--spacing-4')}`, // 6px 8px
      border: `1px solid ${getDesignSystemValue('--color-border-light')}`,
      borderRadius: getDesignSystemValue('--radius-sm'),
      boxShadow: getDesignSystemValue('--shadow-md'),
      fontSize: '11px', // Between xs and sm for compact display
      fontFamily: getDesignSystemValue('--font-primary')
    },
    header: {
      marginBottom: getDesignSystemValue('--spacing-2'), // 4px
      fontWeight: getDesignSystemValue('--font-weight-semibold'),
      fontSize: '10px', // --font-size-xs is 10.4px, so 10px is close
      color: getDesignSystemValue('--color-text-secondary')
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: `${getDesignSystemValue('--spacing-1')} 0` // 2px 0
    },
    colorIndicator: {
      width: '10px',
      height: '10px',
      marginRight: getDesignSystemValue('--spacing-3') // 6px
    },
    label: {
      marginRight: getDesignSystemValue('--spacing-3'), // 6px
      fontWeight: getDesignSystemValue('--font-weight-medium')
    }
  };
  
  // Translate chart type names
  const translateChartType = (type) => {
    return t(`charts.chartTypes.${type}`) || type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (!data || data.length === 0) {
    return (
      <div className="chart-error">
        <p>{t('charts.noData')}</p>
      </div>
    );
  }

  // Get common Nivo props (theme, colors, animation)
  const commonProps = getNivoCommonProps();
  
  // Get column display name helper.
  // Series keys can be numbers (ids, codes); column names are strings.
  const getColumnDisplayName = (column) => {
    if (column == null) return '';
    const columnName = typeof column === 'string' ? column : String(column);
    if (columnDisplayNames && columnDisplayNames[columnName]) {
      return columnDisplayNames[columnName];
    }
    const formatted = columnName.replace(/_/g, ' ');
    const isNorwegian = i18n.language === 'no';
    
    if (isNorwegian) {
      // Norwegian: Only capitalize first letter (sentence case)
      // Example: "antall_åpne_prosjekter" -> "Antall åpne prosjekter"
      return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
    } else {
      // English: Title Case (capitalize each word)
      // Example: "open_projects_count" -> "Open Projects Count"
      return formatted
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
  };

  // Format value for tooltip and table
  const formatValue = (value, column) => {
    // Convert string numbers to actual numbers (database Decimal objects may serialize as strings)
    if (typeof value === 'string' && !isNaN(value) && value !== '') {
      value = parseFloat(value);
    }
    
    if (typeof value === 'number' && !isNaN(value)) {
      return formatValueByType(value, column, columnValueTypes, false);
    }
    return value;
  };

  // Render different chart types
  const renderChart = () => {
    switch (chartType) {
      case 'line':
      case 'area': {
        const nivoData = transformToNivoLine(chartConfig, data);
        const yColumn = chartConfig?.y_axis?.columns?.[0];
        const seriesCount = nivoData.length;
        
        // Use utility functions for common logic
        const xScale = determineXScale(chartConfig, nivoData);
        const hasNegatives = hasNegativeValues(nivoData, null, 'line');
        // For horizontal row legend, use standard margin calculation
        // Single row needs less space than vertical column
        const bottomMargin = calculateLegendMargin(seriesCount);
        
        return (
          <div className="chart-with-legend">
            <ResponsiveLine
              {...commonProps}
              data={nivoData}
              margin={{ top: 20, right: 20, bottom: bottomMargin, left: 60 }}
              xScale={xScale}
              yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
              markers={hasNegatives ? createZeroLineMarker() : []}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                // For numeric X-axes (periods, weeks, months), show only whole numbers
                ...(xScale.type === 'linear' && { format: formatXAxisInteger })
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: (value) => formatYAxisValue(value, yColumn, columnValueTypes)
              }}
              pointSize={6}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              pointLabelYOffset={-12}
              enableArea={chartType === 'area'}
              areaOpacity={chartType === 'area' ? 0.15 : 0}
              useMesh={true}
              enableSlices="x"
              sliceTooltip={({ slice }) => {
                // Find the index of the current point in the data to determine left/right position
                // Use the first series data as reference for positioning
                const firstSeriesData = nivoData[0]?.data || [];
                const currentX = slice.points[0]?.data?.x;
                // Find index by comparing x values (handle both string and number comparisons)
                const pointIndex = firstSeriesData.findIndex(p => String(p.x) === String(currentX));
                
                return (
                  <TooltipWrapper dataIndex={pointIndex} dataLength={firstSeriesData.length}>
                    <div style={tooltipStyles.container}>
                      <div style={tooltipStyles.header}>
                        {getColumnDisplayName(chartConfig?.x_axis?.column || 'x')}: {slice.points[0].data.x}
                      </div>
                      {slice.points.map((point, index) => {
                        // Wide format: seriesId is the column name → use it for correct type lookup.
                        // Long format: all series share a single value column (yColumn).
                        const colForValue = (chartConfig?.data_format === 'long' && chartConfig?.long_format_series)
                          ? yColumn
                          : point.seriesId;
                        return (
                          <div key={point.id || index} style={tooltipStyles.row}>
                            <div
                              style={{
                                ...tooltipStyles.colorIndicator,
                                backgroundColor: point.serieColor
                              }}
                            />
                            <span style={tooltipStyles.label}>
                              {getColumnDisplayName(point.seriesId)}:
                            </span>
                            <strong>{formatValue(point.data.y, colForValue)}</strong>
                          </div>
                        );
                      })}
                    </div>
                  </TooltipWrapper>
                );
              }}
              legends={seriesCount > 1 ? getStandardLegendConfig(seriesCount, 'bottom').map((legend, legendIndex) => {
                // Get colors from the series data
                const chartColors = commonProps.colors;
                return {
                  ...legend,
                  // Use custom data array with display names
                  data: nivoData.map((series, index) => ({
                    id: series.id,
                    label: getColumnDisplayName(series.id),
                    color: series.color || chartColors[index % chartColors.length]
                  }))
                };
              }) : []}
            />
          </div>
        );
      }

      case 'bar': {
        const nivoData = transformToNivoBar(chartConfig, data);
        const keys = getNivoBarKeys(chartConfig, data);
        const indexBy = getNivoBarIndexBy(chartConfig);
        const yColumn = chartConfig?.y_axis?.columns?.[0];
        const seriesCount = keys.length;
        
        // Use utility functions for common logic
        const hasNegatives = hasNegativeValues(nivoData, keys, 'bar');
        // Bottom margin for rotated labels (legend now at top-right, not bottom)
        // Reduced from 140px to 80px - sufficient for rotated X-axis labels without excessive empty space
        const bottomMargin = 80;
        
        // Check if X-axis values are numeric (periods, weeks, etc.)
        const isNumericXAxis = nivoData.length > 0 && typeof nivoData[0][indexBy] === 'number';
        
        return (
          <div className="chart-with-legend">
            <ResponsiveBar
              {...commonProps}
              data={nivoData}
              keys={keys}
              indexBy={indexBy}
              groupMode="grouped"
              margin={{ top: 20, right: 30, bottom: bottomMargin, left: 40 }}
              padding={0.3}
              innerPadding={2}
              valueScale={{ 
                type: 'linear',
                min: hasNegatives ? 'auto' : 0,
                max: 'auto'
              }}
              indexScale={{ type: 'band', round: true }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 15,
                tickRotation: -45,
                legend: null,
                legendPosition: 'middle',
                legendOffset: 40,
                // For numeric X-axes (periods, weeks, months), show only whole numbers
                ...(isNumericXAxis && { format: formatXAxisInteger })
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: (value) => formatYAxisValue(value, yColumn, columnValueTypes)
              }}
              enableGridY={true}
              markers={hasNegatives ? createZeroLineMarker() : []}
              enableLabel={false}
              legends={keys.length > 1 ? (() => {
                // Get chart colors for legend items
                const chartColors = commonProps.colors;
                return getStandardLegendConfig(seriesCount, 'top-right').map(legend => ({
                  ...legend,
                  // Use custom data array with display names instead of dataFrom: 'keys'
                  data: keys.map((key, index) => ({
                    id: key,
                    label: getColumnDisplayName(key),
                    color: chartColors[index % chartColors.length]
                  }))
                }));
              })() : []}
              tooltip={({ id, value, indexValue, color }) => {
                const barIndex = nivoData.findIndex(d => d[indexBy] === indexValue);
                return (
                  <TooltipWrapper dataIndex={barIndex} dataLength={nivoData.length}>
                    <div style={tooltipStyles.container}>
                      <div style={tooltipStyles.header}>
                        {getColumnDisplayName(chartConfig?.x_axis?.column)}: {indexValue}
                      </div>
                      <div style={tooltipStyles.row}>
                        <div
                          style={{
                            ...tooltipStyles.colorIndicator,
                            backgroundColor: color
                          }}
                        />
                        <span style={tooltipStyles.label}>{getColumnDisplayName(id)}:</span>
                        <strong>{formatValue(value, id)}</strong>
                      </div>
                    </div>
                  </TooltipWrapper>
                );
              }}
            />
          </div>
        );
      }

      case 'pie': {
        const nivoData = transformToNivoPie(chartConfig, data);
        const yColumn = chartConfig?.y_axis?.columns?.[0];
        
        return (
          <ResponsivePie
            {...commonProps}
            data={nivoData}
            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            borderWidth={1}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.2]]
            }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: 'color' }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{
              from: 'color',
              modifiers: [['darker', 2]]
            }}
            legends={[
              {
                anchor: 'bottom',
                direction: 'row',
                justify: false,
                translateX: 0,
                translateY: 56,
                itemsSpacing: 10,
                itemWidth: 80,
                itemHeight: 18,
                itemTextColor: '#666',
                itemDirection: 'left-to-right',
                itemOpacity: 1,
                symbolSize: 12,
                symbolShape: 'circle'
              }
            ]}
            tooltip={({ datum }) => (
              <div
                style={{
                  background: 'white',
                  padding: '9px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: datum.color,
                    marginRight: '8px'
                  }}
                />
                <span style={{ marginRight: '8px' }}>{datum.label}:</span>
                <strong>{formatValue(datum.value, yColumn)}</strong>
              </div>
            )}
          />
        );
      }

      case 'table':
      default:
        return (
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  {columns.map((col, index) => (
                    <th key={index}>{getColumnDisplayName(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <td key={colIndex}>
                        {formatValue(row[col], col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  };

  return (
    <div className="chart-renderer">
      {!hideHeader && (
        <div className="chart-header">
          <div className="chart-title-row">
            <div className="chart-header-actions">
              {stepSelector}
              {chartTypes && chartTypes.length > 1 && (
                <div className="inline-chart-type-selector">
                  <select
                    value={chartType}
                    onChange={(e) => onChartTypeChange(e.target.value)}
                    className="chart-type-select"
                  >
                    {chartTypes.map((type) => (
                      <option key={type} value={type}>
                        {translateChartType(type)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {queryId && (
                <AddToDashboardButton 
                  queryId={queryId}
                  currentChartType={chartType}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {renderChart()}
    </div>
  );
};

export default NivoChartRenderer;
